import {Injectable} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
  DocumentChangeAction
} from '@angular/fire/compat/firestore';

import {AngularFireFunctions} from '@angular/fire/compat/functions';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {filter, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import firebase from 'firebase/compat/app';

import {RosterSyncJob, RosterSyncOverviewCounts, RosterSyncOverviewData} from '../interfaces/roster-sync-jobs.model';
import {AuditLogService} from '../../../core/services/audit-log/audit-log.service';
import {AuditLogProductCodes} from '../../../core/services/audit-log/auditLog.model';
import {AnalyticsService} from '../../../core/services/analytics/analytics.service';
import {AuthService} from '../../../core/modules/auth/services/auth.service';
import {DateTimeUtilsService} from '../../../services/utilities/dateTime.utils.service';


@Injectable({
  providedIn: 'root'
})
export class RosterSyncJobsService {
  private syncJobsCollectionRef_: AngularFirestoreCollection<AngularFirestoreDocument<RosterSyncJob>> | undefined;
  private syncJobs$: Observable<DocumentChangeAction<AngularFirestoreDocument<RosterSyncJob>>[]> | undefined;
  private jobsLoaded$ = new BehaviorSubject<boolean>(false);
  private customerId: string | undefined;

  constructor(private afs: AngularFirestore,
              private angularFireFunctions: AngularFireFunctions,
              private auditLogService: AuditLogService,
              private analyticsService: AnalyticsService,
              private authService: AuthService,
              private dateTimeSvc: DateTimeUtilsService
              ) {

  }

  loadForCustomer(customerId: string) {
    if (!this.customerId || this.customerId !== customerId) {
      this.customerId = customerId;
      this.syncJobsCollectionRef_ = this.afs.collection('customers').doc(customerId).collection('rosterSync', ref => ref.orderBy('name'));
      this.syncJobs$ = this.syncJobsCollectionRef_.snapshotChanges().pipe(shareReplay(1));
    }
    this.jobsLoaded$.next(true);
    return this.syncJobs$;
  }

  async jobNameIsUnique(value: string, jobId: string): Promise<boolean> {
    if (!this.syncJobs$) {
      throw new Error('Sync jobs not initialized');
    }
    const currentJobs = await this.syncJobs$.pipe(take(1)).toPromise();
    const duplicates = currentJobs.filter((snap) => {
      // @ts-ignore
      const job = snap.payload.doc.data() as RosterSyncJob;
      return (snap.payload.doc.id !== jobId && job.name.toLowerCase() === value.toLowerCase());
    });
    return !duplicates.length;
  }

  async saveJob(job: RosterSyncJob): Promise<void> {

    // console.log('saveJob', job);
    try {
      await this.clearDiffAnalysis(job, false).pipe(take(1)).toPromise();
    } catch (e) {
      console.warn(e.message);
      throw new Error(e);
    }

    if (!this.syncJobsCollectionRef_) {
      throw new Error('Sync jobs not initialized');
    }
    if (!job.name) {
      throw new Error('Job must have a unique name');
    }
    if (!job.id) {
      const newJobDoc = await this.syncJobsCollectionRef_.doc().get().pipe(take(1)).toPromise();
      job.id = newJobDoc.id;
    }
    if (!job.dates) {
      job.dates = {
        created: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp,
        updated: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp
      };
    } else {
      job.dates = {
        updated: firebase.firestore.FieldValue.serverTimestamp() as firebase.firestore.Timestamp
      };
    }

    if (job.automationSettings.type !== 'manual') {
      const nextExecution = this.dateTimeSvc._calculateNextExecution(job.automationSettings);
      job.dates.nextExecution = firebase.firestore.Timestamp.fromDate(nextExecution);
    } else if (job.active) {
      job.active = false;
    }

    if (job.files && !Array.isArray(job.files)) {
      job.files = Object.values(job.files);
    }

    // Remove duplicate row information, this is only useful in client and can exceed max document size in firestore
    for (const file of job.files) {
      delete file.duplicateRows;
    }

    if (job.automationSettings.type === 'manual') {
      job.active = false;
    }

    // @ts-ignore
    return await this.syncJobsCollectionRef_.doc(job.id).set(job, {merge: true});
  }

  deleteJob(job: RosterSyncJob): Promise<void> {
    return this.syncJobsCollectionRef_.doc(job.id).delete().then(resp => {
      this.auditLogService.addLogEntry({
        productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
        typeId: 21,
        actionId: 138,
        logData: {
          jobName: job.name
        }
      });
    });
  }

  runSyncAnalysis(job: RosterSyncJob) {
    return this.angularFireFunctions.httpsCallable('jobs-initialiseAnalysis')({
      customerId: this.customerId,
      jobId: job.id,
      lscUserId: this.authService.appUser.profile.id,
      lscCustomerId: this.authService.appUser.profile.customerId,
      manualExecution: true
    }).pipe(tap(()=> {
      this.analyticsService.sendEvent({
        action: 'analysis_triggered',
        properties: {
          category: 'roster_sync_job',
          label: job.id
        }
      });

    }));
  }

  clearDiffAnalysis(job: RosterSyncJob, logAction = true) {
    return this.angularFireFunctions.httpsCallable('jobs-clearDiffAnalysis')({
      customerId: this.customerId,
      jobId: job.id
    }).pipe(tap(()=>{
      if(logAction) {
        this.auditLogService.addLogEntry({
          productCode: AuditLogProductCodes.LITTLE_SIS_PREMIUM,
          typeId: 21,
          actionId: 146,
          logData: {jobName: job.name}
        });

        this.analyticsService.sendEvent({
          action: `clear_analysis`,
          properties: {
            category: 'roster_sync_job',
            label: job.id
          }
        });
      }
    }));
  }

  generateNewJobId(): string {
    return this.syncJobsCollectionRef_.doc().ref.id;
  }

  getRosterSyncJobForCustomer(customerId: string, jobId: string): Observable<RosterSyncJob> {
    return this.afs.doc<RosterSyncJob>(`customers/${customerId}/rosterSync/${jobId}`).valueChanges();
  }

  getSyncJobOverview(): Observable<RosterSyncOverviewData> {
    return this.jobsLoaded$.asObservable().pipe(
      filter((loaded) => loaded),
      take(1),
      switchMap(() => this.syncJobs$.pipe(
        take(1),
        map((snaps) => {
          const counts = snaps.reduce((acc, snap) => {
            const data = snap.payload.doc.data() as unknown as RosterSyncJob;
            if (data.active) {
              acc.active++;
            }
            if (data.automationSettings.type === 'manual') {
              acc.manuallyTriggered++;
            } else if (!data.active) {
              acc.paused++;
            }
            return acc;
          }, {active: 0, paused: 0, manuallyTriggered: 0} as RosterSyncOverviewCounts);
          return {
            counts,
            lastUpdated: new Date()
          } as RosterSyncOverviewData;
          })))
    );
  }

}
